import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { motion } from 'framer-motion';

const SkillsVisualization = ({ skills }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('skills-viz');
    if (element) observer.observe(element);

    return () => {
      if (element) observer.unobserve(element);
    };
  }, []);

  if (!skills) return null;

  // Proficiency levels for different skill categories
  const proficiencyLevels = {
    'Machine Learning & AI': 95,
    'Computer Vision': 92,
    'Programming Languages': 88,
    'Frameworks & Libraries': 90,
    'Mathematical Foundations': 85,
    'Tools & Platforms': 82
  };

  return (
    <section id="skills-viz" className="py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Technical Expertise</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Comprehensive skill set with proficiency visualization
          </p>
          <div className="w-20 h-1 bg-blue-900 mx-auto mt-6"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Object.entries(skills).map(([category, skillList], index) => {
            const proficiency = proficiencyLevels[category] || 80;
            
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={isVisible ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="border-2 border-gray-100 hover:border-blue-200 transition-all hover:shadow-xl transform hover:scale-105 h-full">
                  <CardContent className="p-6">
                    {/* Category Header */}
                    <div className="mb-4">
                      <h3 className="font-bold text-lg text-gray-900 mb-2">{category}</h3>
                      
                      {/* Circular Progress */}
                      <div className="flex items-center justify-center mb-4">
                        <div className="relative w-24 h-24">
                          <svg className="transform -rotate-90 w-24 h-24">
                            <circle
                              cx="48"
                              cy="48"
                              r="40"
                              stroke="#e5e7eb"
                              strokeWidth="8"
                              fill="none"
                            />
                            <motion.circle
                              cx="48"
                              cy="48"
                              r="40"
                              stroke="#1e40af"
                              strokeWidth="8"
                              fill="none"
                              strokeLinecap="round"
                              initial={{ strokeDasharray: '251.2 251.2', strokeDashoffset: 251.2 }}
                              animate={isVisible ? {
                                strokeDashoffset: 251.2 - (251.2 * proficiency) / 100
                              } : {}}
                              transition={{ duration: 1.5, delay: index * 0.1 + 0.3 }}
                            />
                          </svg>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <motion.span
                              className="text-xl font-bold text-blue-900"
                              initial={{ opacity: 0 }}
                              animate={isVisible ? { opacity: 1 } : { opacity: 0 }}
                              transition={{ duration: 0.5, delay: index * 0.1 + 0.5 }}
                            >
                              {proficiency}%
                            </motion.span>
                          </div>
                        </div>
                      </div>

                      {/* Proficiency Bar */}
                      <div className="mb-3">
                        <div className="flex justify-between text-xs text-gray-600 mb-1">
                          <span>Proficiency</span>
                          <span className="font-semibold text-blue-900">
                            {proficiency >= 90 ? 'Expert' : proficiency >= 80 ? 'Advanced' : 'Intermediate'}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <motion.div
                            className="bg-gradient-to-r from-blue-900 to-blue-600 h-2 rounded-full"
                            initial={{ width: 0 }}
                            animate={isVisible ? { width: `${proficiency}%` } : { width: 0 }}
                            transition={{ duration: 1.5, delay: index * 0.1 + 0.2 }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Skills List */}
                    <div className="flex flex-wrap gap-2">
                      {skillList.map((skill, idx) => (
                        <motion.span
                          key={idx}
                          className="bg-blue-50 text-blue-900 text-xs px-2 py-1 rounded border border-blue-200"
                          initial={{ opacity: 0, y: 10 }}
                          animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
                          transition={{ duration: 0.3, delay: index * 0.1 + idx * 0.05 }}
                        >
                          {skill}
                        </motion.span>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Overall Statistics */}
        <motion.div
          className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-6"
          initial={{ opacity: 0, y: 20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.5, delay: 0.8 }}
        >
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-6 text-center">
              <div className="text-4xl font-bold text-blue-900 mb-2">
                {Object.values(skills).flat().length}
              </div>
              <div className="text-sm text-gray-700 font-medium">Technical Skills</div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-6 text-center">
              <div className="text-4xl font-bold text-green-900 mb-2">
                {Object.keys(skills).length}
              </div>
              <div className="text-sm text-gray-700 font-medium">Skill Categories</div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="p-6 text-center">
              <div className="text-4xl font-bold text-purple-900 mb-2">89%</div>
              <div className="text-sm text-gray-700 font-medium">Avg. Proficiency</div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
            <CardContent className="p-6 text-center">
              <div className="text-4xl font-bold text-amber-900 mb-2">4</div>
              <div className="text-sm text-gray-700 font-medium">Research Projects</div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>
  );
};

export default SkillsVisualization;
